import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Loader } from '@progress/kendo-react-indicators';
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartTooltip,
  ChartTitle,
} from '@progress/kendo-react-charts';
import { Button } from '@progress/kendo-react-buttons';
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import { fetchApi } from 'services/api';
import { Title } from 'utils/title';
import useCursorLoading from 'utils/useCursorLoading';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import SimpleDateRangePicker from 'components/SimpleDateRangePicker';
import PopupNavBar from 'components/Layout/PopupNavBar';
import { DispatchConfirmationCard, DispatchConfirmationDetail, DispatchConfirmationSummary } from './DispatchConfirmationSection';
import { BillingComplianceCard, BillingComplianceDetail, BillingComplianceSummary } from './BillingComplianceSection';
import { QuoteOfferCallsCard, QuoteOfferCallsDetail, QuoteOfferCallsSummary } from './QuoteOfferCallsSection';

type AgentSummaryCard = {
  AgentType: number;
  AgentTypeName: string;
  TotalSessions: number;
  TracksResolution: boolean;
  ResolvedExceptions: number;
  AvgLatencyMs: number | null;
  AvgConfidence: number | null;
  TotalToolCalls: number;
  TotalInputTokens: number;
  TotalOutputTokens: number;
  EstimatedCostUsd: number;
};

type AgentDailyRow = {
  AgentType: number;
  Day: string;
  Sessions: number;
  Resolved: number;
};

type AgentToolRow = {
  AgentType: number;
  ToolName: string;
  CallCount: number;
};

type QuoteSummary = {
  TotalEmailsProcessed: number;
  TotalQuotes42: number;
  TotalQuotesAll: number;
  TotalDeferrals: number;
  TotalMisquotes: number;
  TotalTimeMisquotes: number;
  AutoQuoteRate: number;
  DeferralRate: number;
};

type QuoteDailyRow = {
  Day: string;
  Quotes42: number;
  QuotesAll: number;
  DeferralThreads: number;
  DeferralEvents: number;
  Misquotes: number;
  TimeMisquotes: number;
};

type QuoteDeferralDetailRow = {
  Day: string;
  TimeUtc: string;
  ThreadID: string | null;
  SenderEmailAddress: string | null;
  Reasons: string | null;
};

type QuoteMisquoteDetailRow = {
  Day: string;
  TimeUtc: string;
  QuoteID: number;
  ThreadID: string | null;
  Reason: string | null;
  Note: string | null;
};

type DashboardData = {
  Agents: AgentSummaryCard[];
  DailyTrend: AgentDailyRow[];
  ToolUsage: AgentToolRow[];
  QuoteSummary: QuoteSummary;
  QuoteDailyTrend: QuoteDailyRow[];
  QuoteDeferralDetail: QuoteDeferralDetailRow[];
  QuoteMisquoteDetail: QuoteMisquoteDetailRow[];
  DispatchConfirmation: DispatchConfirmationSummary;
  BillingCompliance: BillingComplianceSummary;
  QuoteOfferCalls: QuoteOfferCallsSummary | null;
};

// Its outcome data (confirmed/escalated/etc.) is merged into the same card as its technical
// telemetry, so it's excluded from the generic per-agent-type row below to avoid showing twice.
const LOAD_CONFIRMATION_AGENT_TYPE = 5;
const BILLING_COMPLIANCE_AGENT_TYPE = 3;

const QUOTE_URL = (quoteId: number) => `https://app.load1.com/Quote/Index/${quoteId}`;
const FRONT_URL = (threadId: string) => `https://app.frontapp.com/open/${threadId}`;

const AGENT_COLORS: Record<number, string> = {
  1: '#2563eb',
  2: '#10b981',
  3: '#f59e0b',
  4: '#8b5cf6',
};

function rateColor(rate: number): string {
  if (rate >= 85) return '#16a34a';
  if (rate >= 70) return '#eab308';
  return '#dc2626';
}

function resolutionRate(card: AgentSummaryCard): number {
  if (card.TotalSessions === 0) return 0;
  return Math.round((card.ResolvedExceptions / card.TotalSessions) * 1000) / 10;
}

function latencySeconds(ms: number | null): string {
  if (ms == null) return '—';
  return `${(ms / 1000).toFixed(1)}s`;
}

function timeEst(day: string, timeUtc: string): string {
  return dayjs.utc(`${day}T${timeUtc}`).tz('America/New_York').format('HH:mm');
}

// ---- Agent card ----
type AgentCardProps = {
  card: AgentSummaryCard;
  selected: boolean;
  onClick: () => void;
};

function AgentCard({ card, selected, onClick }: AgentCardProps) {
  const accent = AGENT_COLORS[card.AgentType] ?? '#2563eb';
  const borderStyle = selected ? { borderLeft: `4px solid ${accent}` } : {};
  const rate = resolutionRate(card);

  return (
    <div
      className={`card h-100 shadow-sm${selected ? ' border-primary' : ''}`}
      style={{ cursor: 'pointer', ...borderStyle }}
      onClick={onClick}
    >
      <div className="card-body">
        <h6 className="card-title font-weight-bold">{card.AgentTypeName}</h6>
        {card.TracksResolution ? (
          <>
            <div className="mb-2">
              <span className="display-4 font-weight-bold" style={{ color: rateColor(rate), fontSize: '2rem' }}>{rate}%</span>
              <span className="text-muted ml-1" style={{ fontSize: '0.8rem' }}>exceptions resolved</span>
            </div>
            <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              <strong >{card.TotalSessions.toLocaleString()}</strong> sessions &nbsp;
              <strong >{card.ResolvedExceptions.toLocaleString()}</strong> resolved
            </div>
          </>
        ) : (
          <>
            <div className="mb-2">
              <span className="display-4 font-weight-bold" style={{ color: accent, fontSize: '2rem' }}>{card.TotalSessions.toLocaleString()}</span>
              <span className="text-muted ml-1" style={{ fontSize: '0.8rem' }}>sessions</span>
            </div>
            <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
              <strong >{card.TotalToolCalls.toLocaleString()}</strong> tool calls
              {card.AvgConfidence != null && (
                <> &nbsp; <strong >{card.AvgConfidence}</strong>/5 confidence</>
              )}
            </div>
          </>
        )}
        <div className="d-flex align-items-center">
          <span className="badge badge-secondary" title="Token cost tracking is not yet accurate">Cost: N/A</span>
          <span className="badge badge-light border ml-1">{latencySeconds(card.AvgLatencyMs)}/turn</span>
        </div>
      </div>
    </div>
  );
}

// ---- Agent detail panel ----
type AgentDetailProps = {
  card: AgentSummaryCard;
  dailyRows: AgentDailyRow[];
  toolRows: AgentToolRow[];
  onClose: () => void;
};

function AgentDetail({ card, dailyRows, toolRows, onClose }: AgentDetailProps) {
  const color = AGENT_COLORS[card.AgentType] ?? '#2563eb';

  const dates = useMemo(() => dailyRows.map(r => dayjs(r.Day).toDate()), [dailyRows]);
  const sessionData = useMemo(() => dailyRows.map(r => r.Sessions), [dailyRows]);
  const rateData = useMemo(() =>
    dailyRows.map(r => r.Sessions > 0 ? Math.round((r.Resolved / r.Sessions) * 1000) / 10 : null),
    [dailyRows]);

  const topTools = useMemo(() => toolRows.slice(0, 8), [toolRows]);
  const toolNames = useMemo(() => topTools.map(t => t.ToolName), [topTools]);
  const toolCounts = useMemo(() => topTools.map(t => t.CallCount), [topTools]);

  return (
    <div className="card mt-3 shadow-sm">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <span
            className="rounded-circle mr-2"
            style={{ display: 'inline-block', width: 10, height: 10, background: color }}
          />
          <strong>{card.AgentTypeName}</strong>
          <span className="text-muted ml-2" style={{ fontSize: '0.85rem' }}>— selected date range</span>
        </div>
        <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-lg-6 mb-3">
            <Chart transitions={false} style={{ height: 240 }}>
              <ChartTitle text={card.TracksResolution ? 'Sessions & Resolution %' : 'Sessions'} />
              <ChartArea background="transparent" />
              <ChartLegend visible={card.TracksResolution} position="bottom" />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  type="date"
                  baseUnit="days"
                  categories={dates}
                  majorGridLines={{ visible: false }}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem name="sessions" line={{ visible: false }} labels={{ format: '{0:n0}' }} />
                {card.TracksResolution && (
                  <ChartValueAxisItem
                    name="rate"
                    min={-5}
                    max={100}
                    labels={{ format: '{0}%' }}
                  />
                )}
              </ChartValueAxis>
              <ChartSeries>
                <ChartSeriesItem
                  type="column"
                  data={sessionData}
                  name="Sessions"
                  axis="sessions"
                  color={color}
                  tooltip={{ format: '{0:n0}' }}
                  gap={2.5}
                  border={{ width: 0 }}
                />
                {card.TracksResolution && (
                  <ChartSeriesItem
                    type="line"
                    data={rateData}
                    name="Resolution %"
                    axis="rate"
                    color="#0f172a"
                    tooltip={{ format: '{0}%' }}
                  />
                )}
              </ChartSeries>
              <ChartTooltip />
            </Chart>
          </div>
          <div className="col-lg-6 mb-3">
            {topTools.length === 0 ? (
              <p className="text-muted">No tool data for this period.</p>
            ) : (
              <Chart transitions={false} style={{ height: Math.max(160, topTools.length * 28 + 60) }}>
                <ChartTitle text="Top Tools Used" />
                <ChartArea background="transparent" />
                <ChartCategoryAxis>
                  <ChartCategoryAxisItem
                    categories={toolNames}
                    majorGridLines={{ visible: false }}
                    line={{ visible: false }}
                  />
                </ChartCategoryAxis>
                <ChartValueAxis>
                  <ChartValueAxisItem visible={false} />
                </ChartValueAxis>
                <ChartSeries>
                  <ChartSeriesItem
                    type="bar"
                    data={toolCounts}
                    color={color}
                    gap={0.8}
                    border={{ width: 0 }}
                    labels={{ visible: true, format: '{0:n0}', background: 'transparent' }}
                  />
                </ChartSeries>
                <ChartTooltip format="{0:n0}" />
              </Chart>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Quote feature card ----
type QuoteCardProps = {
  summary: QuoteSummary;
  selected: boolean;
  onClick: () => void;
};

function QuoteFeatureCard({ summary, selected, onClick }: QuoteCardProps) {
  const color = rateColor(summary.AutoQuoteRate);
  const borderStyle = selected ? { borderLeft: '4px solid #2563eb' } : {};

  return (
    <div
      className={`card h-100 shadow-sm${selected ? ' border-primary' : ''}`}
      style={{ cursor: 'pointer', ...borderStyle }}
      onClick={onClick}
    >
      <div className="card-body">
        <h6 className="card-title font-weight-bold">Quote Creation from Email</h6>
        <div className="mb-2">
          <span className="font-weight-bold" style={{ color, fontSize: '2rem' }}>{summary.AutoQuoteRate}%</span>
          <span className="text-muted ml-1" style={{ fontSize: '0.8rem' }}>auto-quoted</span>
        </div>
        <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
          <strong >{summary.TotalEmailsProcessed.toLocaleString()}</strong> emails &nbsp;
          <strong >{summary.TotalDeferrals}</strong> deferred &nbsp;
          <strong >{summary.TotalMisquotes}</strong> misquotes flagged
        </div>
        <div className="d-flex align-items-center">
          <span className="badge badge-secondary" title="Token cost tracking is not yet accurate">Cost: N/A</span>
        </div>
      </div>
    </div>
  );
}

// ---- Quote detail panel ----
type QuoteDetailProps = {
  summary: QuoteSummary;
  dailyRows: QuoteDailyRow[];
  deferralRows: QuoteDeferralDetailRow[];
  misquoteRows: QuoteMisquoteDetailRow[];
  onClose: () => void;
};

function QuoteDetail({ summary, dailyRows, deferralRows, misquoteRows, onClose }: QuoteDetailProps) {
  const dates = useMemo(() => dailyRows.map(r => dayjs(r.Day).toDate()), [dailyRows]);
  const emailData = useMemo(() => dailyRows.map(r => r.Quotes42 + r.DeferralThreads), [dailyRows]);
  const deferralPct = useMemo(() =>
    dailyRows.map(r => {
      const total = r.Quotes42 + r.DeferralThreads;
      return total > 0 ? Math.round((r.DeferralThreads / total) * 1000) / 10 : null;
    }), [dailyRows]);
  // Misquotes are counts (flagged that day), not a rate — a rate of a large email volume
  // renders as a flat line and hides the trend.
  const misquoteCount = useMemo(() => dailyRows.map(r => r.Misquotes), [dailyRows]);

  // Days that actually have deferrals or misquotes, newest first.
  const detailDays = useMemo(() => {
    const set = new Set<string>();
    deferralRows.forEach(r => set.add(r.Day));
    misquoteRows.forEach(r => set.add(r.Day));
    return Array.from(set).sort().reverse();
  }, [deferralRows, misquoteRows]);

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const activeDay = selectedDay && detailDays.includes(selectedDay) ? selectedDay : (detailDays[0] ?? null);

  const dayDeferrals = useMemo(
    () => deferralRows.filter(r => r.Day === activeDay),
    [deferralRows, activeDay]);
  const dayMisquotes = useMemo(
    () => misquoteRows.filter(r => r.Day === activeDay),
    [misquoteRows, activeDay]);

  return (
    <div className="card mt-3 shadow-sm">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <span className="rounded-circle mr-2" style={{ display: 'inline-block', width: 10, height: 10, background: '#2563eb' }} />
          <strong>Quote Creation from Email</strong>
          <span className="text-muted ml-2" style={{ fontSize: '0.85rem' }}>— selected date range</span>
        </div>
        <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <span className="badge mr-2 p-2" style={{ background: '#dcfce7', color: '#16a34a' }}>
            {summary.AutoQuoteRate}% Auto-Quoted
          </span>
          <span className="badge mr-2 p-2" style={{ background: '#ffedd5', color: '#c2410c' }}>
            {summary.DeferralRate}% Deferred to Human
          </span>
          <span className="badge p-2" style={{ background: '#fee2e2', color: '#b91c1c' }}>
            {summary.TotalMisquotes} Misquotes Flagged
          </span>
        </div>
        {dailyRows.length === 0 ? (
          <p className="text-muted">No data for this period.</p>
        ) : (
          <>
            <Chart
              transitions={false}
              style={{ height: 320 }}
              onSeriesClick={(e) => setSelectedDay(dayjs(e.category as Date).format('YYYY-MM-DD'))}
            >
              <ChartTitle text="Email Volume, Deferral Rate &amp; Misquotes Flagged" />
              <ChartArea background="transparent" />
              <ChartLegend position="bottom" />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  type="date"
                  baseUnit="days"
                  categories={dates}
                  majorGridLines={{ visible: false }}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem name="misquotes" min={0} labels={{ format: '{0:n0}' }} />
                <ChartValueAxisItem name="pct" labels={{ format: '{0}%' }} />
                <ChartValueAxisItem name="emails" line={{ visible: false }} labels={{ format: '{0:n0}' }} />
              </ChartValueAxis>
              <ChartSeries>
                <ChartSeriesItem
                  type="column"
                  data={emailData}
                  name="Emails Processed"
                  axis="emails"
                  color="#2563eb"
                  tooltip={{ format: '{0:n0}' }}
                  gap={2.5}
                  border={{ width: 0 }}
                />
                <ChartSeriesItem
                  type="line"
                  data={deferralPct}
                  name="Deferral %"
                  axis="pct"
                  color="#f97316"
                  markers={{ visible: true, size: 5 }}
                  tooltip={{ format: '{0}%' }}
                />
                <ChartSeriesItem
                  type="line"
                  data={misquoteCount}
                  name="Misquotes Flagged"
                  axis="misquotes"
                  color="#dc2626"
                  markers={{ visible: true, size: 5 }}
                  tooltip={{ format: '{0:n0}' }}
                />
              </ChartSeries>
              <ChartTooltip />
            </Chart>
            <p className="text-muted mt-2" style={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
              Emails processed and deferral rate are bucketed by the day the quote was created (auto-quotes are
              Source 42). Misquotes Flagged counts every quote a dispatcher flagged that day, across all reasons
              and quote types; the table below lists them.
            </p>
          </>
        )}

        {/* Per-day detail tables */}
        <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap">
              <h6 className="text-muted text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}>Deferrals &amp; Misquotes by Day</h6>
              <div className="d-flex align-items-center">
                <small className="text-muted mr-2">Click a chart day or pick:</small>
                <select
                  className="form-control form-control-sm"
                  style={{ width: 'auto' }}
                  value={activeDay ?? ''}
                  onChange={(e) => setSelectedDay(e.target.value)}
                >
                  {detailDays.length === 0 && <option value="">No data</option>}
                  {detailDays.map(d => (
                    <option key={d} value={d}>{dayjs(d).format('ddd, MMM D, YYYY')}</option>
                  ))}
                </select>
              </div>
            </div>

            {activeDay == null ? (
              <p className="text-muted mb-0">No deferrals or misquotes in this period.</p>
            ) : (
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <div className="font-weight-bold mb-1" style={{ color: '#c2410c' }}>
                    Deferred to Human <span className="badge badge-light border ml-1">{dayDeferrals.length}</span>
                  </div>
                  {dayDeferrals.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: '0.85rem' }}>None on this day.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover" style={{ fontSize: '0.8rem' }}>
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Sender</th>
                            <th>Reason(s)</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayDeferrals.map((r, i) => (
                            <tr key={i}>
                              <td className="text-nowrap">{timeEst(r.Day, r.TimeUtc)}</td>
                              <td>{r.SenderEmailAddress || '—'}</td>
                              <td>{r.Reasons || '—'}</td>
                              <td className="text-right text-nowrap">
                                {r.ThreadID ? (
                                  <a href={FRONT_URL(r.ThreadID)} target="_blank" rel="noopener noreferrer">Front</a>
                                ) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="col-lg-6 mb-3">
                  <div className="font-weight-bold mb-1" style={{ color: '#b91c1c' }}>
                    Misquotes Flagged (all reasons) <span className="badge badge-light border ml-1">{dayMisquotes.length}</span>
                  </div>
                  {dayMisquotes.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: '0.85rem' }}>None on this day.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover" style={{ fontSize: '0.8rem' }}>
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Quote</th>
                            <th>Reason</th>
                            <th>Note</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayMisquotes.map((r, i) => (
                            <tr key={i}>
                              <td className="text-nowrap">{timeEst(r.Day, r.TimeUtc)}</td>
                              <td className="text-nowrap">
                                <a href={QUOTE_URL(r.QuoteID)} target="_blank" rel="noopener noreferrer">#{r.QuoteID}</a>
                              </td>
                              <td>{r.Reason || '—'}</td>
                              <td>{r.Note || '—'}</td>
                              <td className="text-right text-nowrap">
                                {r.ThreadID ? (
                                  <a href={FRONT_URL(r.ThreadID)} target="_blank" rel="noopener noreferrer">Front</a>
                                ) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

// ---- Main dashboard ----
const AIAgentDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(dayjs().subtract(6, 'day').startOf('day').toDate());
  const [toDate, setToDate] = useState(dayjs().startOf('day').toDate());
  const [data, setData] = useState<DashboardData | null>(null);
  // One selection across all cards: an agent type number, or a named section.
  const [openPanel, setOpenPanel] = useState<number | 'quote' | 'dispatchConfirmation' | 'billingCompliance' | 'quoteOfferCalls' | null>(null);

  useCursorLoading(loading);
  const { status, currentTheme } = useThemeSwitcher();
  const darkClass = currentTheme === 'dark' ? ' ai-ops-dark' : '';

  const refresh = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    try {
      const result = await fetchApi('/api/AIAgentDashboard', {
        fromDate: dayjs(fromDate).format('YYYY-MM-DD'),
        toDate: dayjs(toDate).format('YYYY-MM-DD'),
      }, 'POST');
      setData(result);
    } catch (err) {
      console.error('Error fetching AI agent dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { refresh(); }, [refresh]);

  const agentDailyRows = useCallback((agentType: number) =>
    (data?.DailyTrend ?? []).filter(r => r.AgentType === agentType),
    [data]);

  const agentToolRows = useCallback((agentType: number) =>
    (data?.ToolUsage ?? []).filter(r => r.AgentType === agentType),
    [data]);

  const selectedAgentCard = typeof openPanel === 'number'
    ? data?.Agents.find(a => a.AgentType === openPanel) ?? null
    : null;

  const loadConfirmationAgent = data?.Agents.find(a => a.AgentType === LOAD_CONFIRMATION_AGENT_TYPE) ?? null;
  const billingComplianceAgent = data?.Agents.find(a => a.AgentType === BILLING_COMPLIANCE_AGENT_TYPE) ?? null;
  const genericAgentCards = (data?.Agents ?? []).filter(a =>
    a.AgentType !== LOAD_CONFIRMATION_AGENT_TYPE && a.AgentType !== BILLING_COMPLIANCE_AGENT_TYPE);

  if (status !== 'loaded') {
    return <div>Loading...</div>;
  }

  return (
    <React.Fragment>
      <PopupNavBar />
      <Title string="AI Operations" />
      {/* Scoped to this page only: a near-black backdrop is what makes Bootstrap's default
          text-muted (gray-600) actually readable, since the gap that matters is contrast
          against the card, not the text color itself. Kept local via this class rather than
          touching bootstrap-dark-theme.scss, which every other page in the app also uses. */}
      <style>{`
        .ai-ops-dark { background: #0a0a0a; min-height: 100vh; color: #ededed; }
        .ai-ops-dark .card { background: #141414; border-color: rgba(255,255,255,0.08); }
        .ai-ops-dark .card-header { background: rgba(255,255,255,0.03); border-color: rgba(255,255,255,0.08); }
        .ai-ops-dark .text-muted { color: #a3a3a3 !important; }
        .ai-ops-dark table { color: #ededed; }
        .ai-ops-dark .table-hover tbody tr:hover { background: rgba(255,255,255,0.05); }
        .ai-ops-dark a { color: #60a5fa; }
        .ai-ops-dark .badge-light { background: rgba(255,255,255,0.08); color: #ededed; border-color: rgba(255,255,255,0.12) !important; }
        .ai-ops-dark .badge-secondary { background: rgba(255,255,255,0.1); color: #a3a3a3; }
        .ai-ops-dark .form-control { background: #1a1a1a; border-color: rgba(255,255,255,0.14); color: #ededed; }
        .ai-ops-dark .btn-outline-secondary { color: #a3a3a3; border-color: rgba(255,255,255,0.18); }
        .ai-ops-dark .btn-outline-secondary:hover { background: rgba(255,255,255,0.08); color: #ededed; }
        .ai-ops-dark .close { color: #ededed; text-shadow: none; }
        .ai-ops-dark hr, .ai-ops-dark .border-top { border-color: rgba(255,255,255,0.08) !important; }
      `}</style>
      <nav className="navbar navbar-dark bg-dark">
        <a className="navbar-brand" href="/">
          AI Operations {loading && <Loader type="pulsing" className="p-0" />}
        </a>
        <ul className="nav navbar-nav navbar-right">
          <Button
            title="Refresh"
            svgIcon={arrowRotateCwIcon}
            disabled={loading}
            onClick={refresh}
          />
        </ul>
      </nav>

      <div className={`container-fluid pt-3 pb-4${darkClass}`}>
        <div className="row mb-3">
          <div className="col-md-4">
            <SimpleDateRangePicker
              inputProps={{ disabled: loading }}
              value={{ start: fromDate, end: toDate }}
              onChange={(e) => {
                setFromDate(e.start);
                setToDate(e.end);
              }}
            />
          </div>
        </div>

        {/* All AI reports as one unified card row */}
        <div className="mb-2">
          <h5 className="mb-0">AI Reports</h5>
          <small className="text-muted">Click a card for details</small>
        </div>
        <div className="row mb-2">
          {genericAgentCards.map(card => (
            <div className="col-md-3 mb-3" key={card.AgentType}>
              <AgentCard
                card={card}
                selected={openPanel === card.AgentType}
                onClick={() => setOpenPanel(openPanel === card.AgentType ? null : card.AgentType)}
              />
            </div>
          ))}
          {data?.QuoteSummary && (
            <div className="col-md-3 mb-3">
              <QuoteFeatureCard
                summary={data.QuoteSummary}
                selected={openPanel === 'quote'}
                onClick={() => setOpenPanel(openPanel === 'quote' ? null : 'quote')}
              />
            </div>
          )}
          {data?.BillingCompliance && (
            <div className="col-md-3 mb-3">
              <BillingComplianceCard
                summary={data.BillingCompliance}
                avgLatencyMs={billingComplianceAgent?.AvgLatencyMs}
                selected={openPanel === 'billingCompliance'}
                onClick={() => setOpenPanel(openPanel === 'billingCompliance' ? null : 'billingCompliance')}
              />
            </div>
          )}
          {data?.DispatchConfirmation && (
            <div className="col-md-3 mb-3">
              <DispatchConfirmationCard
                summary={data.DispatchConfirmation}
                selected={openPanel === 'dispatchConfirmation'}
                onClick={() => setOpenPanel(openPanel === 'dispatchConfirmation' ? null : 'dispatchConfirmation')}
              />
            </div>
          )}
          {data?.QuoteOfferCalls && (
            <div className="col-md-3 mb-3">
              <QuoteOfferCallsCard
                summary={data.QuoteOfferCalls}
                from={fromDate}
                to={toDate}
                selected={openPanel === 'quoteOfferCalls'}
                onClick={() => setOpenPanel(openPanel === 'quoteOfferCalls' ? null : 'quoteOfferCalls')}
              />
            </div>
          )}
        </div>
        {selectedAgentCard && (
          <AgentDetail
            card={selectedAgentCard}
            dailyRows={agentDailyRows(selectedAgentCard.AgentType)}
            toolRows={agentToolRows(selectedAgentCard.AgentType)}
            onClose={() => setOpenPanel(null)}
          />
        )}
        {openPanel === 'quote' && data?.QuoteSummary && (
          <QuoteDetail
            summary={data.QuoteSummary}
            dailyRows={data.QuoteDailyTrend}
            deferralRows={data.QuoteDeferralDetail ?? []}
            misquoteRows={data.QuoteMisquoteDetail ?? []}
            onClose={() => setOpenPanel(null)}
          />
        )}
        {openPanel === 'billingCompliance' && data?.BillingCompliance && (
          <BillingComplianceDetail
            summary={data.BillingCompliance}
            onClose={() => setOpenPanel(null)}
          />
        )}
        {openPanel === 'dispatchConfirmation' && data?.DispatchConfirmation && (
          <DispatchConfirmationDetail
            summary={data.DispatchConfirmation}
            agent={loadConfirmationAgent}
            onClose={() => setOpenPanel(null)}
          />
        )}
        {openPanel === 'quoteOfferCalls' && data?.QuoteOfferCalls && (
          <QuoteOfferCallsDetail
            summary={data.QuoteOfferCalls}
            from={fromDate}
            to={toDate}
            onClose={() => setOpenPanel(null)}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default AIAgentDashboard;
